import type { SVGProps } from 'react'

/**
 * The icon set.
 *
 * The design draws hugeicons stroke-rounded at 24×24 with a 2px stroke
 * (design-spec.md §5.8). §8.7 leaves the package licence unconfirmed, so these
 * are original paths drawn to the same grid and weight rather than copies —
 * swap in the package here once the licence is settled and nothing else
 * changes.
 */

export type IconName =
  | 'arrow-left'
  | 'chevron-down'
  | 'chevron-left'
  | 'lock'
  | 'home'
  | 'chart'
  | 'swap'
  | 'wallet'
  | 'coins'
  | 'user-scan'
  | 'hourglass'
  | 'cloud-upload'
  | 'close'
  | 'camera'
  | 'search'
  | 'eye'
  | 'eye-off'
  | 'transfer'
  | 'arrow-up-circle'
  | 'arrow-down-circle'
  | 'more-horizontal'
  | 'more-vertical'
  | 'menu'
  | 'message'
  | 'swap-vertical'
  | 'check'
  | 'arrow-down-left'
  | 'gift'
  | 'activity'
  | 'trash'
  | 'info'
  | 'bell'
  | 'notification'
  | 'user'
  | 'user-pen'
  | 'credit-card'
  | 'check-circle'
  | 'log-out'
  | 'alert'
  | 'shield-tick'
  | 'copy'
  | 'phone'

const PATHS: Record<IconName, React.ReactNode> = {
  'arrow-left': (
    <>
      <path d="M20 12H4" />
      <path d="M10 6 4 12l6 6" />
    </>
  ),
  'chevron-down': <path d="m6 9 6 6 6-6" />,
  // The "go to" affordance on the hub and the per-asset page (`202:597`).
  'arrow-down-left': (
    <>
      <path d="M18 6 6 18" />
      <path d="M15 18H6V9" />
    </>
  ),
  gift: (
    <>
      <path d="M20 12v8a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-8" />
      <path d="M3 8h18v4H3z" />
      <path d="M12 8v13" />
      <path d="M12 8S10.5 3 8 3a2.5 2.5 0 0 0 0 5h4Z" />
      <path d="M12 8s1.5-5 4-5a2.5 2.5 0 0 1 0 5h-4Z" />
    </>
  ),
  'chevron-left': <path d="m15 6-6 6 6 6" />,
  lock: (
    <>
      <rect x="4" y="10" width="16" height="11" rx="3.5" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
      <path d="M12 14.5v2.5" />
    </>
  ),
  home: (
    <>
      <path d="M3.5 10.2 12 3.8l8.5 6.4V19a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2z" />
      <path d="M9.5 21v-5.5h5V21" />
    </>
  ),
  chart: (
    <>
      <path d="M3 3v15a3 3 0 0 0 3 3h15" />
      <path d="m7 16 3.5-4.5 3 2.5L18 8" />
    </>
  ),
  swap: (
    <>
      <circle cx="8" cy="8" r="4.5" />
      <circle cx="16" cy="16" r="4.5" />
      <path d="M12.5 5.5H19l-2-2M11.5 18.5H5l2 2" />
    </>
  ),
  wallet: (
    <>
      <rect x="3" y="6" width="18" height="14" rx="3.5" />
      <path d="M3 10h13a2 2 0 0 1 2 2v1a2 2 0 0 1-2 2H3" />
      <path d="M17.5 6V4.8a1.8 1.8 0 0 0-2.3-1.7L5 5.6" />
    </>
  ),
  coins: (
    <>
      <circle cx="9.5" cy="9.5" r="6" />
      <path d="M14.9 6.6a6 6 0 1 1-5.3 10.9" />
    </>
  ),
  'user-scan': (
    <>
      <path d="M3 8V5.5A2.5 2.5 0 0 1 5.5 3H8M16 3h2.5A2.5 2.5 0 0 1 21 5.5V8M21 16v2.5a2.5 2.5 0 0 1-2.5 2.5H16M8 21H5.5A2.5 2.5 0 0 1 3 18.5V16" />
      <circle cx="12" cy="10.5" r="2.5" />
      <path d="M8 17a4 4 0 0 1 8 0" />
    </>
  ),
  hourglass: (
    <>
      <path d="M6 3h12M6 21h12" />
      <path d="M7.5 3v3.2a3 3 0 0 0 .9 2.1L12 12l-3.6 3.7a3 3 0 0 0-.9 2.1V21" />
      <path d="M16.5 3v3.2a3 3 0 0 1-.9 2.1L12 12l3.6 3.7a3 3 0 0 1 .9 2.1V21" />
    </>
  ),
  'cloud-upload': (
    <>
      <path d="M7 18a4 4 0 0 1-.4-8 5.5 5.5 0 0 1 10.6 1.2A3.5 3.5 0 0 1 17 18" />
      <path d="M12 21v-8M9 15.5 12 12.5l3 3" />
    </>
  ),
  close: <path d="m6 6 12 12M18 6 6 18" />,
  camera: (
    <>
      <path d="M3 9.5A2.5 2.5 0 0 1 5.5 7h1.2l1.1-2h6.4l1.1 2h1.2A2.5 2.5 0 0 1 19 9.5v7A2.5 2.5 0 0 1 16.5 19h-11A2.5 2.5 0 0 1 3 16.5z" />
      <circle cx="11" cy="13" r="3.2" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.6-3.6" />
    </>
  ),
  eye: (
    <>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  'eye-off': (
    <>
      <path d="M3 3l18 18" />
      <path d="M10.6 6a8.6 8.6 0 0 1 1.4-.1c6 0 9.5 6.1 9.5 6.1a15 15 0 0 1-3 3.6M6.6 7.9A15 15 0 0 0 2.5 12S6 18.1 12 18.1a8.9 8.9 0 0 0 3.4-.7" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
    </>
  ),
  transfer: (
    <>
      <path d="M4 8h14l-3-3M20 16H6l3 3" />
    </>
  ),
  'arrow-up-circle': (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 16.5v-9M8.5 11 12 7.5l3.5 3.5" />
    </>
  ),
  'arrow-down-circle': (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5v9M8.5 13l3.5 3.5 3.5-3.5" />
    </>
  ),
  'more-horizontal': (
    <>
      <circle cx="5" cy="12" r="1.4" fill="currentColor" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" />
      <circle cx="19" cy="12" r="1.4" fill="currentColor" />
    </>
  ),
  // The row menu on the coin list (`740:963`).
  'more-vertical': (
    <>
      <circle cx="12" cy="5" r="1.4" fill="currentColor" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" />
      <circle cx="12" cy="19" r="1.4" fill="currentColor" />
    </>
  ),
  menu: (
    <>
      <path d="M4 7h16M4 12h12M4 17h16" />
    </>
  ),
  message: (
    <>
      <path d="M20.5 11.5a8 8 0 0 1-11.6 7.1L4 20l1.4-4.6A8 8 0 1 1 20.5 11.5z" />
    </>
  ),
  'swap-vertical': (
    <>
      <path d="M8 4v16M8 20l-3-3M8 20l3-3" />
      <path d="M16 20V4M16 4l-3 3M16 4l3 3" />
    </>
  ),
  check: <path d="m5 12.5 4.5 4.5L19 7.5" />,
  // The chart shortcut on the buy/sell form (`731:219`): a pulse line.
  activity: <path d="M3 12h3.5l2.5-6 4.5 12 2.5-6H21" />,
  // The delete action on an alert row (`731:11`).
  trash: (
    <>
      <path d="M4 6.5h16" />
      <path d="M9.5 6.5V4.8a1.3 1.3 0 0 1 1.3-1.3h2.4a1.3 1.3 0 0 1 1.3 1.3v1.7" />
      <path d="m6 6.5.9 12.6a2 2 0 0 0 2 1.9h6.2a2 2 0 0 0 2-1.9l.9-12.6" />
      <path d="M10 11v5.5M14 11v5.5" />
    </>
  ),
  // The hint beside the advanced-mode fields (`731:219`).
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5.5" />
      <path d="M12 7.6v.1" />
    </>
  ),
  // «هشدار» on the coin actions sheet (`740:1525`).
  bell: (
    <>
      <path d="M6 9.5a6 6 0 0 1 12 0c0 4.6 1.7 6.5 2.5 7.2H3.5C4.3 16 6 14.1 6 9.5Z" />
      <path d="M10 20a2.1 2.1 0 0 0 4 0" />
    </>
  ),
  // The inbox (`924:709`): its rows' tinted circle, and the top bar's bell.
  // The frame's `notification-bing`, a bell with a stroke inside it, scaled
  // from 20 onto this grid like the profile glyphs below.
  notification: (
    <>
      <path d="M12 6.44v3.33" />
      <path d="M12.02 2C8.34 2 5.36 4.98 5.36 8.66v2.1c0 .68-.28 1.7-.63 2.28l-1.27 2.12c-.78 1.31-.24 2.77 1.2 3.25a23.34 23.34 0 0 0 14.73 0 2.22 2.22 0 0 0 1.2-3.25l-1.27-2.12c-.35-.58-.63-1.61-.63-2.28v-2.1C18.68 5 15.68 2 12.02 2Z" />
      <path d="M15.33 18.82a3.33 3.33 0 0 1-6.66 0" />
    </>
  ),
  /* The profile frames (`924:904`) draw these at 15–20 with a 1.5 stroke; the
     paths are theirs, scaled onto this grid. */
  // The avatar when there is no name to take a letter from. No frame draws
  // it: it is `user-pen`'s figure, centred, without the pen.
  user: (
    <>
      <circle cx="12" cy="7" r="4" />
      <path d="M5 21v-2a4 4 0 0 1 4-4h6a4 4 0 0 1 4 4v2" />
    </>
  ),
  // «اطلاعات شخصی» on the hub (`924:818`).
  'user-pen': (
    <>
      <path d="M11.5 15H7a4 4 0 0 0-4 4v2" />
      <path d="M21.38 16.63a2.12 2.12 0 0 0-3-3l-4.02 4.01a2 2 0 0 0-.5.85l-.84 2.87a.5.5 0 0 0 .62.62l2.87-.84a2 2 0 0 0 .85-.5z" />
      <circle cx="10" cy="7" r="4" />
    </>
  ),
  // «کارت‌های بانکی» on the hub (`924:818`).
  'credit-card': (
    <>
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="M2 10h20" />
    </>
  ),
  // The verification pill on the hub (`924:818`).
  'check-circle': (
    <>
      <path d="M21.8 10A10 10 0 1 1 17 3.34" />
      <path d="m9 11 3 3L22 4" />
    </>
  ),
  // «خروج از حساب» (`924:818`).
  'log-out': (
    <>
      <path d="m16 17 5-5-5-5" />
      <path d="M21 12H9" />
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    </>
  ),
  // The add-card sheet's warning (`924:131`), the one filled glyph here: the
  // frame draws a solid triangle with the mark knocked out of it.
  alert: (
    <path
      fill="currentColor"
      stroke="none"
      fillRule="evenodd"
      d="M21.76 15.92 15.36 4.4C14.5 2.85 13.31 2 12 2s-2.5.85-3.36 2.4L2.24 15.92c-.81 1.47-.9 2.88-.25 3.99.65 1.11 1.93 1.72 3.61 1.72h12.8c1.68 0 2.96-.61 3.61-1.72.65-1.11.56-2.53-.25-3.99ZM11.25 9a.75.75 0 0 1 1.5 0v5a.75.75 0 0 1-1.5 0V9ZM12 16a1 1 0 1 1 0 2 1 1 0 0 1 0-2Z"
    />
  ),
  // The score card on «امنیت حساب» (`924:648`), which draws it at 1.5.
  'shield-tick': (
    <>
      <path d="M12 2.75 4.75 5.6v5.65c0 4.45 3 8.25 7.25 9.95 4.25-1.7 7.25-5.5 7.25-9.95V5.6L12 2.75Z" />
      <path d="m8.9 12 2.2 2.2 4-4.2" />
    </>
  ),
  // «کپی» on «دعوت از دوستان» (`924:277`), drawn at 20 and 1.5: a sheet in
  // front of the one it copies.
  copy: (
    <>
      <rect x="9" y="9" width="11" height="11" rx="1.5" />
      <path d="M15 9V5.5A1.5 1.5 0 0 0 13.5 4h-8A1.5 1.5 0 0 0 4 5.5v8A1.5 1.5 0 0 0 5.5 15H9" />
    </>
  ),
  // «تماس تلفنی» on «پشتیبانی». No frame draws it: a handset, earpiece at
  // the top left, drawn to this grid.
  phone: (
    <path d="M5.4 3h3.1l1.9 5-2.5 1.7a13.8 13.8 0 0 0 6.4 6.4l1.7-2.5 5 1.9v3.1a2.4 2.4 0 0 1-2.5 2.4A17.4 17.4 0 0 1 3 5.5a2.4 2.4 0 0 1 2.4-2.5Z" />
  ),
}

interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName
  size?: number
  strokeWidth?: number
}

export function Icon({ name, size = 24, strokeWidth = 2, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {PATHS[name]}
    </svg>
  )
}
